// features/hive/ui/HiveBodyAnalytics/HiveBodyAnalytics.tsx
import {useCallback, useEffect, useMemo, useState, type ReactElement} from 'react';
import {
  AnalyticsHeader,
  AnalyticsParent,
  AnalyticsWrapper,
  AnalyticsArea,
  Row,
  HiddenRow,
  DateInputInSegmentWrapper,
} from './styles';
import {DefaultChartWidget} from 'widgets/chart';
import {hiveChartsMocks} from './mocks';
import {chartValue, LegendItem, XTickFormatter} from 'widgets/chart/model/types';
import {InputDate, Segment, SegmentedControl} from '@atomaro/ui-kit';
import {AnalyticsCard} from 'features/hive/ui/HiveBodyAnalytics/AnalyticsCard.tsx';
import {HappyChapter} from 'shared/ui/icons/chapterIcons/happyChapter.tsx';
import TemperatureChapter from 'shared/ui/icons/chapterIcons/temperatureChapter.tsx';
import HumidityChapter from 'shared/ui/icons/chapterIcons/humidityChapter.tsx';
import Co2Chapter from 'shared/ui/icons/chapterIcons/co2Chapter.tsx';
import WeightChapter from 'shared/ui/icons/chapterIcons/weightChapter.tsx';
import StrengthChapter from 'shared/ui/icons/chapterIcons/strengthChapter.tsx';
import {useLocation, useParams} from 'react-router-dom';
import {withMask} from 'use-mask-input';
import {COLORS as COLORS_THEME} from '../../../../shared/consts';
import {$api} from 'shared/api';
import {useApiariesStore} from 'entities/apiaries';
import {useCurrentHiveStore} from 'entities/currentHive';

export type UIPeriod = 'today' | 'yesterday' | 'week' | 'month' | 'year' | 'custom';
export type PeriodKey =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'month'
  | 'year'
  | 'custom_1_2_days'
  | 'custom_3_7_days'
  | 'custom_8_46_days'
  | 'custom_47_plus';
export type ScaleKey = 'hours' | 'days' | 'weeks' | 'months';

const allowedScales: Record<PeriodKey, ScaleKey[]> = {
  today: ['hours'],
  yesterday: ['hours'],
  week: ['hours', 'days'],
  month: ['hours', 'days', 'weeks'],
  year: ['days', 'weeks', 'months'],
  custom_1_2_days: ['hours'],
  custom_3_7_days: ['hours', 'days'],
  custom_8_46_days: ['hours', 'days', 'weeks'],
  custom_47_plus: ['days', 'weeks', 'months'],
};

const COLORS = {
  marks: '#6B8AFF',
  bar: 'var(--accent-muted, rgba(255, 114, 65, 1))',
  bar2: 'var(--neutral-muted, rgba(181, 183, 192, 1))',
  line: 'var(--accent-default, rgba(255, 79, 18, 1))',
  diffPos: 'var(--accent-default, rgba(255, 79, 18, 1))',
  diffNeg: COLORS_THEME.BG.SURFACE3,
  threshold: '#FF4F12',
};

type ChartId = 'happiness' | 'temperature' | 'humidity' | 'co2' | 'weight' | 'state';

type StoredMark = {
  hiveId: string | number;
  chartId: ChartId;
  marks: {color: string; markName: string; markValue: number; isDisplayable: boolean}[];
};
type AbsThreshold = {
  hiveId: string | number;
  chartId: ChartId;
  min: number | null;
  max: number | null;
  isDisplayable: boolean;
};
type GainThreshold = {
  hiveId: string | number;
  chartId: ChartId;
  min: number | null;
  max: number | null;
};

const LS_KEYS = {
  marks: 'hiveMarks',
  abs: 'hiveAbsThresholds',
  gain: 'hiveGainThresholds',
};

const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;
const RU_MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
] as const;
const ddmm = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;

type CustomRange = {start: number; end: number};
type AnalyticsInterval = 'hour' | 'day' | 'week' | 'month';
type RemotePoint = {
  time?: string;
  timestamp?: string;
  date?: string;
  value?: number | string | null;
};
type HiveAnalyticsResponse = Partial<
  Record<'temperature' | 'humidity' | 'co2' | 'weight', RemotePoint[]>
>;

const normalizeRange = (start: Date, end: Date): CustomRange => {
  const startDate = new Date(start);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);
  return {start: startDate.getTime(), end: endDate.getTime()};
};

const buildSixHourlyAlignedTicks = (start: number, end: number) => {
  const ticks: number[] = [start];
  const startDate = new Date(start);
  const alignedStart = new Date(startDate);
  alignedStart.setHours(0, 0, 0, 0);

  let current = alignedStart.getTime();
  while (current < end) {
    if (current > start) ticks.push(current);
    current += 6 * HOUR_MS;
  }

  if (ticks[ticks.length - 1] !== end) ticks.push(end);
  return ticks;
};

const buildDailyTicks = (start: number, end: number) => {
  const ticks: number[] = [start];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  if (cursor.getTime() <= start) cursor.setDate(cursor.getDate() + 1);

  while (cursor.getTime() < end) {
    ticks.push(cursor.getTime());
    cursor.setDate(cursor.getDate() + 1);
  }

  if (ticks[ticks.length - 1] !== end) ticks.push(end);
  return ticks;
};

const buildFixedIntervalTicks = (start: number, end: number, interval: number) => {
  const ticks: number[] = [start];
  let current = start + interval;
  while (current < end) {
    ticks.push(current);
    current += interval;
  }
  if (ticks[ticks.length - 1] !== end) ticks.push(end);
  return ticks;
};

const buildYearTicks = (start: number, end: number) => {
  const ticks: number[] = [start];
  const cursor = new Date(start);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);
  const month = cursor.getMonth();
  const monthsToNextQuarter = (3 - (month % 3)) % 3 || 3;
  cursor.setMonth(month + monthsToNextQuarter);

  while (cursor.getTime() < end) {
    ticks.push(cursor.getTime());
    cursor.setMonth(cursor.getMonth() + 3);
  }

  if (ticks[ticks.length - 1] !== end) ticks.push(end);
  return ticks;
};

const buildTicksForPeriod = (period: PeriodKey, start: number, end: number) => {
  if (!(isFinite(start) && isFinite(end) && end > start)) return undefined;

  if (period === 'today' || period === 'yesterday' || period === 'custom_1_2_days') {
    return buildSixHourlyAlignedTicks(start, end);
  }
  if (period === 'week' || period === 'custom_3_7_days') {
    return buildDailyTicks(start, end);
  }
  if (period === 'month' || period === 'custom_8_46_days') {
    return buildFixedIntervalTicks(start, end, 7 * DAY_MS);
  }
  if (period === 'year' || period === 'custom_47_plus') {
    return buildYearTicks(start, end);
  }
  return undefined;
};

const formatTickPartsForPeriod = (
  period: PeriodKey,
  ms: number,
  index?: number,
  ticks?: number[]
): string[] => {
  const d = new Date(ms);

  if (period === 'today' || period === 'yesterday' || period === 'custom_1_2_days') {
    return [`${String(d.getHours()).padStart(2, '0')}:00`];
  }
  if (
    period === 'week' ||
    period === 'month' ||
    period === 'custom_3_7_days' ||
    period === 'custom_8_46_days'
  ) {
    return [ddmm(d)];
  }
  if (period === 'year') {
    const isEdge =
      typeof index === 'number' && Array.isArray(ticks)
        ? index === 0 || index === ticks.length - 1
        : false;
    if (isEdge) return [RU_MONTHS[d.getMonth()]];
    const quarter = Math.floor(d.getMonth() / 3) + 1;
    return [`${quarter} кв ${d.getFullYear()}`];
  }
  if (period === 'custom_47_plus') {
    const quarter = Math.floor(d.getMonth() / 3) + 1;
    return [`${quarter} квартал`, String(d.getFullYear())];
  }
  return [ddmm(d)];
};

const getRequestRange = (uiPeriod: UIPeriod, customRange: CustomRange | null) => {
  if (uiPeriod === 'custom' && customRange) {
    const from = new Date(customRange.start);
    const to = new Date(customRange.end);
    const diffDays = Math.max(1, Math.ceil((customRange.end - customRange.start) / DAY_MS));
    const interval: AnalyticsInterval =
      diffDays <= 2 ? 'hour' : diffDays <= 60 ? 'day' : diffDays <= 365 ? 'week' : 'month';
    return {from, to, interval};
  }

  const now = Date.now();
  let fromMs = now - DAY_MS;
  let toMs = now;

  switch (uiPeriod) {
    case 'today':
      fromMs = now - DAY_MS;
      toMs = now;
      break;
    case 'yesterday': {
      const start = new Date(now);
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      fromMs = start.getTime();
      toMs = fromMs + DAY_MS - 1;
      break;
    }
    case 'week':
      fromMs = now - 7 * DAY_MS;
      toMs = now;
      break;
    case 'month':
      fromMs = now - 30 * DAY_MS;
      toMs = now;
      break;
    case 'year':
      fromMs = now - 365 * DAY_MS;
      toMs = now;
      break;
  }

  const diffDays = Math.max(1, Math.ceil((toMs - fromMs) / DAY_MS));
  const interval: AnalyticsInterval =
    diffDays <= 2 ? 'hour' : diffDays <= 60 ? 'day' : diffDays <= 365 ? 'week' : 'month';

  return {from: new Date(fromMs), to: new Date(toMs), interval};
};

const mapRemoteSeries = (arr?: RemotePoint[]) =>
  (arr ?? [])
    .map((point) => {
      const date = point.time ?? point.timestamp ?? point.date ?? null;
      const value = point.value ?? 0;
      if (!date) return null;
      const num = typeof value === 'number' ? value : Number(value ?? 0);
      return {date, value: Number.isFinite(num) ? num : 0};
    })
    .filter((item): item is {date: string; value: number} => !!item)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

const parseDateMs = (value?: string | null) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const filterSeriesFrom = <T extends {date: string; value: number}>(
  data: T[] | undefined,
  minMs: number | null
): T[] => {
  if (!data?.length) return [];
  if (minMs == null) return data;
  return data.filter((point) => {
    const pointMs = new Date(point.date).getTime();
    return Number.isFinite(pointMs) && pointMs >= minMs;
  });
};

const clampRangeFrom = (
  range: {from: Date; to: Date; interval: AnalyticsInterval},
  minMs: number | null
) => {
  if (minMs == null || minMs <= range.from.getTime()) return range;

  const toMs = range.to.getTime();
  const fromMs = Math.min(minMs, toMs);
  return {...range, from: new Date(fromMs)};
};

function readLS<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as T[];
  } catch {
    return [];
  }
}

function toSeries(arr?: {date: string; value: number}[] | undefined): chartValue[] {
  return (arr ?? []).map((p) => ({x: new Date(p.date).getTime(), y: p.value}));
}

function bundleFor(metric: keyof typeof hiveChartsMocks, period: PeriodKey, scale: ScaleKey) {
  // Принудительно используем нужный масштаб для периода
  let actualScale = scale;
  if (period === 'week' && scale === 'hours') {
    actualScale = 'days';
  }

  if (period === 'month') {
    actualScale = 'days';
  }

  if (period === 'year') {
    actualScale = 'months';
  }

  return hiveChartsMocks[metric]?.[period]?.[actualScale];
}

// function makeItems(period: PeriodKey): IDropdownMenuItem[] {
//     const map: Record<ScaleKey, string> = { hours: "Часы", days: "Дни", weeks: "Недели", months: "Месяцы" };
//     return allowedScales[period].map((k) => ({ key: k, value: map[k] }));
// }

function classifyCustomRangeDays(days: number): PeriodKey {
  if (days <= 2) return 'custom_1_2_days';
  if (days <= 7) return 'custom_3_7_days';
  if (days <= 46) return 'custom_8_46_days';
  return 'custom_47_plus';
}

function hasMetric(
  metric: keyof typeof hiveChartsMocks,
  period: PeriodKey,
  scale: ScaleKey
): boolean {
  const b = hiveChartsMocks[metric]?.[period]?.[scale];
  return !!b && !!b.data && b.data.length > 0;
}

function avgValue(data?: {date: string; value: number}[]): number {
  if (!data || data.length === 0) return 0;
  const s = data.reduce((acc, p) => acc + (p?.value ?? 0), 0);
  return Number((s / data.length).toFixed(1));
}

function periodDelta(data?: {date: string; value: number}[]): number {
  if (!data || data.length < 2) return 0;
  const first = data[0]?.value ?? 0;
  const last = data[data.length - 1]?.value ?? 0;
  return Number((last - first).toFixed(1));
}

function lastTimeStr(data?: {date: string; value: number}[]): string {
  if (!data || data.length === 0) return '';
  const last = new Date(data[data.length - 1].date);
  const hh = String(last.getHours()).padStart(2, '0');
  const mm = String(last.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function legendSeries(label: string, color: string): LegendItem {
  return {label, color};
}

export const HiveBodyAnalytics = () => {
  const {id} = useParams<{id?: string}>();
  const hiveId = id || '';
  const apiaryId = useApiariesStore((s) => s.currentApiaryId);
  const currentHive = useCurrentHiveStore((s) => s.currentHive);

  const [uiPeriod, setUiPeriod] = useState<UIPeriod>('today');
  const [period, setPeriod] = useState<PeriodKey>('today');
  const [scale, setScale] = useState<ScaleKey>('hours');
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const [isSmartByDevices, setIsSmartByDevices] = useState(false);
  const [remoteSeries, setRemoteSeries] = useState<
    Partial<Record<'temperature' | 'humidity' | 'co2' | 'weight', {date: string; value: number}[]>>
  >({});

  useEffect(() => {
    if (!apiaryId || !hiveId) {
      setIsSmartByDevices(false);
      return;
    }

    let cancelled = false;

    const fetchDevices = async () => {
      try {
        const response = await $api.get<any[]>('/collector/devices', {
          params: {apiaryId},
        });
        const devices = Array.isArray(response?.data) ? response.data : [];
        const countForHive = devices.filter((d) => {
          const deviceHiveId = d?.hiveId ?? d?.hive_id ?? d?.hive?.id ?? d?.clientId ?? null;
          return String(deviceHiveId ?? '') === String(hiveId);
        }).length;

        if (!cancelled) {
          setIsSmartByDevices(countForHive > 0);
        }
      } catch {
        if (!cancelled) {
          setIsSmartByDevices(false);
        }
      }
    };

    fetchDevices();
    return () => {
      cancelled = true;
    };
  }, [apiaryId, hiveId]);

  useEffect(() => {
    const list = allowedScales[period];
    if (!list.includes(scale)) setScale(list[0]);
  }, [period, scale]);

  const requestRange = useMemo(
    () => getRequestRange(uiPeriod, customRange),
    [uiPeriod, customRange]
  );
  const hiveCreatedAtMs = useMemo(
    () =>
      currentHive && String(currentHive.id) === String(hiveId)
        ? parseDateMs(currentHive.createdAt)
        : null,
    [currentHive, hiveId]
  );
  const minSelectableDate = useMemo(() => {
    if (hiveCreatedAtMs == null) return null;
    const date = new Date(hiveCreatedAtMs);
    date.setHours(0, 0, 0, 0);
    return date;
  }, [hiveCreatedAtMs]);
  const effectiveRequestRange = useMemo(
    () => clampRangeFrom(requestRange, hiveCreatedAtMs),
    [requestRange, hiveCreatedAtMs]
  );

  useEffect(() => {
    if (!apiaryId || !hiveId || !isSmartByDevices) {
      setRemoteSeries({});
      return;
    }

    let cancelled = false;

    const fetchAnalytics = async () => {
      try {
        const response = await $api.get<HiveAnalyticsResponse>('/collector/analytics/summary', {
          params: {
            hiveId,
            apiaryId,
            from: effectiveRequestRange.from.toISOString(),
            to: effectiveRequestRange.to.toISOString(),
            interval: effectiveRequestRange.interval,
          },
        });
        if (!cancelled) {
          setRemoteSeries({
            temperature: mapRemoteSeries(response?.data?.temperature),
            humidity: mapRemoteSeries(response?.data?.humidity),
            co2: mapRemoteSeries(response?.data?.co2),
            weight: mapRemoteSeries(response?.data?.weight),
          });
        }
      } catch {
        if (!cancelled) {
          setRemoteSeries({});
        }
      }
    };

    fetchAnalytics();
    return () => {
      cancelled = true;
    };
  }, [apiaryId, hiveId, isSmartByDevices, effectiveRequestRange]);

  // const selectItems = useMemo(() => makeItems(period), [period]);
  const normalizedScale = useMemo<ScaleKey>(() => {
    const list = allowedScales[period];
    return list.includes(scale) ? scale : list[0];
  }, [period, scale]);

  const happiness = bundleFor('happiness', period, normalizedScale);
  const happinessData = useMemo(
    () => filterSeriesFrom(happiness?.data, hiveCreatedAtMs),
    [happiness, hiveCreatedAtMs]
  );
  const temperatureData = remoteSeries.temperature ?? [];
  const humidityData = remoteSeries.humidity ?? [];
  const co2Data = remoteSeries.co2 ?? [];
  const weightData = remoteSeries.weight ?? [];

  const familyScale = useMemo<ScaleKey | null>(() => {
    const order = [normalizedScale, ...allowedScales[period].filter((s) => s !== normalizedScale)];
    for (const s of order) {
      if (hasMetric('strength', period, s) && hasMetric('brood', period, s)) return s;
    }
    for (const s of order) {
      if (hasMetric('strength', period, s) || hasMetric('brood', period, s)) return s;
    }
    return null;
  }, [period, normalizedScale]);

  const strength = familyScale ? bundleFor('strength', period, familyScale) : undefined;
  const brood = familyScale ? bundleFor('brood', period, familyScale) : undefined;
  const strengthData = useMemo(
    () => filterSeriesFrom(strength?.data, hiveCreatedAtMs),
    [strength, hiveCreatedAtMs]
  );
  const broodData = useMemo(
    () => filterSeriesFrom(brood?.data, hiveCreatedAtMs),
    [brood, hiveCreatedAtMs]
  );

  const marksLS = useMemo(() => readLS<StoredMark>(LS_KEYS.marks), []);
  const absLS = useMemo(() => readLS<AbsThreshold>(LS_KEYS.abs), []);
  const gainLS = useMemo(() => readLS<GainThreshold>(LS_KEYS.gain), []);

  const getMarks = (chartId: ChartId) =>
    (
      marksLS.find((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId)?.marks ?? []
    ).filter((m) => m.isDisplayable);

  const getAbs = (chartId: ChartId) =>
    absLS.find((x) => String(x.hiveId) === String(hiveId) && x.chartId === chartId) ?? {
      hiveId,
      chartId,
      min: null,
      max: null,
      isDisplayable: false,
    };

  const getGain = () =>
    gainLS.find((x) => String(x.hiveId) === String(hiveId) && x.chartId === 'weight') ?? {
      hiveId,
      chartId: 'weight',
      min: null,
      max: null,
    };

  const makeThresholds = (abs: AbsThreshold) => {
    const arr: number[] = [];
    if (abs.isDisplayable) {
      if (typeof abs.min === 'number') arr.push(abs.min);
      if (typeof abs.max === 'number') arr.push(abs.max);
    }
    return arr;
  };

  const marksHappiness = getMarks('happiness');
  const marksTemperature = getMarks('temperature');
  const marksHumidity = getMarks('humidity');
  const marksCo2 = getMarks('co2');
  const marksWeight = getMarks('weight');
  const marksState = getMarks('state');

  const absHappiness = getAbs('happiness');
  const absTemperature = getAbs('temperature');
  const absHumidity = getAbs('humidity');
  const absCo2 = getAbs('co2');
  const absWeight = getAbs('weight');
  const absState = getAbs('state');

  const gainWeight = getGain();

  const legendsFromMarks = (arr: {markName: string}[]): LegendItem[] =>
    arr.map((m) => ({label: m.markName, color: COLORS.marks}));

  const thresholdLegend = (abs: AbsThreshold, label: string): LegendItem[] =>
    abs.isDisplayable && (typeof abs.min === 'number' || typeof abs.max === 'number')
      ? [{label, color: COLORS.threshold}]
      : [];

  const happinessLegend: LegendItem[] = [
    legendSeries('Индекс счастья', COLORS.bar),
    ...legendsFromMarks(marksHappiness),
    ...thresholdLegend(absHappiness, 'Пороговые значения'),
  ];
  const temperatureLegend: LegendItem[] = [
    legendSeries('Температура', COLORS.line),
    ...legendsFromMarks(marksTemperature),
    ...thresholdLegend(absTemperature, 'Пороговые значения температуры'),
  ];
  const humidityLegend: LegendItem[] = [
    legendSeries('Влажность', COLORS.line),
    ...legendsFromMarks(marksHumidity),
    ...thresholdLegend(absHumidity, 'Пороговые значения влажности'),
  ];
  const co2Legend: LegendItem[] = [
    legendSeries('CO₂', COLORS.line),
    ...legendsFromMarks(marksCo2),
    ...thresholdLegend(absCo2, 'Пороговые значения CO₂'),
  ];
  const weightLegend: LegendItem[] = [
    legendSeries('Абсолютная масса', COLORS.bar),
    {label: 'Положительный привес', color: COLORS.diffPos},
    {label: 'Отрицательный привес', color: COLORS.diffNeg},
    ...legendsFromMarks(marksWeight),
    ...thresholdLegend(absWeight, 'Пороги абсолютной массы'),
    ...(typeof gainWeight.min === 'number' || typeof gainWeight.max === 'number'
      ? [{label: 'Пороги привеса', color: COLORS.threshold}]
      : []),
  ];
  const familyLegend: LegendItem[] = [
    legendSeries('Сила', COLORS.bar),
    legendSeries('Расплод', COLORS.bar2),
    ...legendsFromMarks(marksState),
    ...thresholdLegend(absState, 'Пороговые значения'),
  ];

  const happinessHasData = Boolean(happinessData.length);
  const temperatureHasData = Boolean(temperatureData.length);
  const humidityHasData = Boolean(humidityData.length);
  const co2HasData = Boolean(co2Data.length);
  const weightHasData = Boolean(weightData.length);
  const familyHasData = Boolean(strengthData.length || broodData.length);
  const sparklineCurrentDate = useMemo(
    () => (uiPeriod === 'today' ? Date.now() : undefined),
    [uiPeriod]
  );
  const axisRangeOverride = useMemo<CustomRange | null>(() => {
    const start = effectiveRequestRange.from.getTime();
    const end = effectiveRequestRange.to.getTime();
    if (uiPeriod === 'today' && start === requestRange.from.getTime()) return null;
    return {
      start,
      end,
    };
  }, [uiPeriod, requestRange, effectiveRequestRange]);
  const axisXTicks = useMemo(
    () =>
      axisRangeOverride
        ? buildTicksForPeriod(period, axisRangeOverride.start, axisRangeOverride.end)
        : undefined,
    [axisRangeOverride, period]
  );
  const axisXDomain = useMemo(
    () =>
      axisRangeOverride
        ? {
            min: axisRangeOverride.start,
            max: axisRangeOverride.end,
          }
        : undefined,
    [axisRangeOverride]
  );
  const axisTickFormatter = useCallback<XTickFormatter>(
    (value, index, ticks) => formatTickPartsForPeriod(period, value, index, ticks),
    [period]
  );

  const happinessChart: ReactElement = (
    <DefaultChartWidget
      isActive={happinessHasData}
      type="column"
      tooltipText={[{title: 'Индекс счастья', dim: 'баллов', isSign: false}]}
      width="100%"
      height={220}
      data={toSeries(happinessData)}
      marks={marksHappiness.map((m) => m.markValue)}
      thresholds={makeThresholds(absHappiness)}
      showAxes
      fillFuture
      periodForFill="day"
      xTicks={axisXTicks}
      xTickFormatter={axisTickFormatter}
      xDomain={axisXDomain}
      currentDate={sparklineCurrentDate}
      key={`happiness-${period}-${normalizedScale}`}
    />
  );

  const temperatureChart: ReactElement = (
    <DefaultChartWidget
      isActive={temperatureHasData}
      type="sparkline"
      tooltipText={[{title: 'Температура', dim: '°C', isSign: false}]}
      width="100%"
      height={220}
      data={toSeries(temperatureData)}
      marks={marksTemperature.map((m) => m.markValue)}
      thresholds={makeThresholds(absTemperature)}
      showAxes
      fillFuture
      periodForFill="day"
      xTicks={axisXTicks}
      xTickFormatter={axisTickFormatter}
      xDomain={axisXDomain}
      currentDate={sparklineCurrentDate}
      key={`temperature-${period}-${normalizedScale}`}
    />
  );

  const humidityChart: ReactElement = (
    <DefaultChartWidget
      isActive={humidityHasData}
      type="sparkline"
      width="100%"
      tooltipText={[{title: 'Влажность', dim: '%', isSign: false}]}
      height={220}
      data={toSeries(humidityData)}
      marks={marksHumidity.map((m) => m.markValue)}
      thresholds={makeThresholds(absHumidity)}
      showAxes
      fillFuture
      periodForFill="day"
      xTicks={axisXTicks}
      xTickFormatter={axisTickFormatter}
      xDomain={axisXDomain}
      currentDate={sparklineCurrentDate}
      key={`humidity-${period}-${normalizedScale}`}
    />
  );

  const co2Chart: ReactElement = (
    <DefaultChartWidget
      isActive={co2HasData}
      type="sparkline"
      tooltipText={[{title: 'CO₂', dim: 'ppm', isSign: false}]}
      width="100%"
      height={220}
      data={toSeries(co2Data)}
      marks={marksCo2.map((m) => m.markValue)}
      thresholds={makeThresholds(absCo2)}
      showAxes
      fillFuture
      periodForFill="day"
      xTicks={axisXTicks}
      xTickFormatter={axisTickFormatter}
      xDomain={axisXDomain}
      currentDate={sparklineCurrentDate}
      key={`co2-${period}-${normalizedScale}`}
    />
  );

  const weightChart: ReactElement = (
    <DefaultChartWidget
      isActive={weightHasData}
      type="column"
      tooltipText={[
        {title: 'Масса улья', dim: 'кг', isSign: false},
        {title: 'Привес', dim: 'кг', isSign: true},
      ]}
      width="100%"
      height={220}
      data={toSeries(weightData)}
      marks={marksWeight.map((m) => m.markValue)}
      thresholds={makeThresholds(absWeight)}
      showAxes
      fillFuture
      periodForFill="day"
      showDiff
      xTicks={axisXTicks}
      xTickFormatter={axisTickFormatter}
      xDomain={axisXDomain}
      currentDate={sparklineCurrentDate}
      key={`weight-${period}-${normalizedScale}`}
    />
  );

  const familyChart: ReactElement = (
    <DefaultChartWidget
      isActive={familyHasData}
      type="column2"
      width="100%"
      tooltipText={[
        {title: 'Сила', dim: '', isSign: false},
        {title: 'Расплод', dim: '', isSign: false},
      ]}
      height={220}
      data={toSeries(strengthData)}
      data2={toSeries(broodData)}
      marks={marksState.map((m) => m.markValue)}
      thresholds={makeThresholds(absState)}
      showAxes
      fillFuture
      periodForFill="day"
      seriesLabels={{first: 'Сила', second: 'Расплод'}}
      xTicks={axisXTicks}
      xTickFormatter={axisTickFormatter}
      xDomain={axisXDomain}
      currentDate={sparklineCurrentDate}
      key={`family-${period}-${familyScale ?? normalizedScale}`}
    />
  );

  const topPrimaries = [happinessChart, temperatureChart, humidityChart, co2Chart].filter(
    Boolean
  ) as ReactElement[];
  const bottomCandidates = [weightChart, familyChart].filter(Boolean) as ReactElement[];

  const isSmart = isSmartByDevices;

  const topFilled: ReactElement[] = (() => {
    if (!isSmart) {
      return [happinessChart, familyChart].filter(Boolean).slice(0, 2) as ReactElement[];
    }
    const arr = [...topPrimaries].slice(0, 4);
    if (arr.length > 0 && arr.length < 3) {
      for (const n of bottomCandidates) {
        if (arr.length >= 3) break;
        if (n) arr.push(n);
      }
    }
    return arr.slice(0, 4);
  })();

  const promoted = new Set(topFilled);
  const bottomFinal = isSmart
    ? ([weightChart, familyChart].filter((n) => n && !promoted.has(n)) as ReactElement[])
    : [];

  const topCount = topFilled.length;
  const bottomCount = bottomFinal.length;

  const fracTop = !isSmart ? 0.5 : topCount >= 4 ? 0.25 : topCount === 3 ? 1 / 3 : 0.5;
  const totalBottomItems = bottomCount;
  const fracBottom = Math.min(0.5, 1 / Math.max(1, totalBottomItems));

  const [top1, top2, top3, top4] = topFilled;

  const bottomWeight =
    isSmart && !promoted.has(weightChart as any) ? (weightChart as ReactElement | null) : null;
  const bottomFamily =
    isSmart && !promoted.has(familyChart as any) ? (familyChart as ReactElement | null) : null;

  const happinessAvg = avgValue(happinessData);
  const tempAvg = avgValue(temperatureData);
  const humAvg = avgValue(humidityData);
  const co2Avg = avgValue(co2Data);
  const weightAvg = avgValue(weightData);
  const strengthAvg = avgValue(strengthData);
  const broodAvg = avgValue(broodData);

  const weightDelta = periodDelta(weightData);
  const weightDeltaStr = weightDelta > 0 ? `+${weightDelta}` : `${weightDelta}`;

  const happinessTime = lastTimeStr(happinessData);
  const tempTime = lastTimeStr(temperatureData);
  const humTime = lastTimeStr(humidityData);
  const co2Time = lastTimeStr(co2Data);
  const weightTime = lastTimeStr(weightData);
  const familyTime = lastTimeStr(strengthData.length ? strengthData : broodData);

  const currentPath = useLocation().pathname;

  return (
    <AnalyticsParent>
      <AnalyticsHeader>
        <SegmentedControl
          size="m"
          variant="secondary"
          value={uiPeriod}
          onChange={(val) => {
            const key = val as UIPeriod;
            setUiPeriod(key);
            if (key !== 'custom') {
              setCustomRange(null);
              setPeriod(key as PeriodKey);
            }
          }}
        >
          <Segment index="today" label="День" />
          {/*<Segment index="yesterday" label="Вчера" />*/}
          <Segment index="week" label="Неделя" />
          <Segment index="month" label="Месяц" />
          <Segment index="year" label="Год" />
          <DateInputInSegmentWrapper className={uiPeriod === 'custom' ? 'active' : ''}>
            <InputDate
              placeholder="Период"
              ref={withMask(['99.99.9999', '99.99.9999 - 99.99.9999'])}
              maxDate={new Date()}
              minDate={minSelectableDate ?? undefined}
              isRange
              onChange={(start, end) => {
                const s = start instanceof Date ? start : null;
                const e = end instanceof Date ? end : null;
                if (s && e) {
                  const ms = e.getTime() - s.getTime();
                  const days = Math.max(1, Math.ceil(ms / (24 * 3600_000)));
                  const bucket = classifyCustomRangeDays(days);
                  setCustomRange(normalizeRange(s, e));
                  setUiPeriod('custom');
                  setPeriod(bucket);
                } else {
                  setCustomRange(null);
                }
              }}
              onBlur={() => {}}
            />
          </DateInputInSegmentWrapper>
        </SegmentedControl>

        {/* <Select name="interval" value={normalizedScale} onChange={(v) => setScale(v as ScaleKey)} placement="bottom" items={selectItems} /> */}
      </AnalyticsHeader>

      <AnalyticsWrapper>
        {topCount === 0 ? (
          <HiddenRow />
        ) : (
          <Row itemCount={topCount} rowKind="top">
            {top1 ? (
              <AnalyticsArea frac={fracTop}>
                <AnalyticsCard
                  id="happiness"
                  icon={<HappyChapter />}
                  title="Индекс счастья"
                  lastUpdate={happinessTime}
                  link={currentPath + '/happiness'}
                  averages={[
                    {name: 'В среднем', value: happinessAvg, unit: 'баллов', status: 'green'},
                  ]}
                  legend={happinessLegend}
                >
                  {happinessChart}
                </AnalyticsCard>
              </AnalyticsArea>
            ) : null}

            {top2 ? (
              <AnalyticsArea frac={fracTop}>
                <AnalyticsCard
                  id={isSmart ? 'temperature' : 'state'}
                  icon={isSmart ? <TemperatureChapter /> : <StrengthChapter />}
                  title={isSmart ? 'Температура' : 'Состояние семьи'}
                  lastUpdate={isSmart ? tempTime : familyTime}
                  link={currentPath + (isSmart ? '/temperature' : '/state')}
                  averages={
                    isSmart
                      ? [{name: 'В среднем', value: tempAvg, unit: '°C', status: 'green'}]
                      : [
                          {name: 'Сила', value: strengthAvg, unit: 'баллов', status: 'green'},
                          {name: 'Расплод', value: broodAvg, unit: 'баллов', status: 'green'},
                        ]
                  }
                  legend={isSmart ? temperatureLegend : familyLegend}
                >
                  {isSmart ? temperatureChart : familyChart}
                </AnalyticsCard>
              </AnalyticsArea>
            ) : null}

            {isSmart && top3 ? (
              <AnalyticsArea frac={fracTop}>
                <AnalyticsCard
                  id="humidity"
                  icon={<HumidityChapter />}
                  title="Влажность"
                  lastUpdate={humTime}
                  link={currentPath + '/humidity'}
                  averages={[
                    {name: 'В среднем', value: Math.round(humAvg), unit: '%', status: 'green'},
                  ]}
                  legend={humidityLegend}
                >
                  {humidityChart}
                </AnalyticsCard>
              </AnalyticsArea>
            ) : null}

            {isSmart && top4 ? (
              <AnalyticsArea frac={fracTop}>
                <AnalyticsCard
                  id="co2"
                  icon={<Co2Chapter />}
                  title="CO₂"
                  lastUpdate={co2Time}
                  link={currentPath + '/co2'}
                  averages={[
                    {name: 'В среднем', value: Math.round(co2Avg), unit: 'ppm', status: 'green'},
                  ]}
                  legend={co2Legend}
                >
                  {co2Chart}
                </AnalyticsCard>
              </AnalyticsArea>
            ) : null}
          </Row>
        )}

        {isSmart ? (
          <Row itemCount={totalBottomItems} rowKind="bottom">
            {bottomWeight ? (
              <AnalyticsArea frac={fracBottom}>
                <AnalyticsCard
                  id="weight"
                  icon={<WeightChapter />}
                  title="Масса улья"
                  lastUpdate={weightTime}
                  link={currentPath + '/weight'}
                  averages={[
                    {name: 'Масса в среднем', value: weightAvg, unit: 'кг', status: 'green'},
                    {name: 'Привес за период', value: weightDeltaStr, unit: 'кг', status: 'green'},
                  ]}
                  legend={weightLegend}
                >
                  {weightChart}
                </AnalyticsCard>
              </AnalyticsArea>
            ) : null}

            {bottomFamily ? (
              <AnalyticsArea frac={fracBottom}>
                <AnalyticsCard
                  id="state"
                  icon={<StrengthChapter />}
                  title="Состояние семьи"
                  lastUpdate={familyTime}
                  link={currentPath + '/state'}
                  averages={[
                    {name: 'Сила', value: strengthAvg, unit: 'баллов', status: 'green'},
                    {name: 'Расплод', value: broodAvg, unit: 'баллов', status: 'green'},
                  ]}
                  legend={familyLegend}
                >
                  {familyChart}
                </AnalyticsCard>
              </AnalyticsArea>
            ) : null}
          </Row>
        ) : null}
      </AnalyticsWrapper>
    </AnalyticsParent>
  );
};
